import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { GraphQLCommitment, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { usePaymentMethodsWithIssues } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { getCommitmentsTotalDue } from '@sola/supporter-sdk/utils/commitments.ts';
export interface PaymentMethodAlertProps {
  // Determines if a simple or verbose alert is displayed.
  isSimple?: boolean;
}
export const shouldRenderPaymentMethodAlert = ({
  paymentMethodsWithIssues,
  associatedCommitments,
  isSimple = false
}: {
  paymentMethodsWithIssues: GraphQLSupporterPaymentMethod[];
  associatedCommitments: GraphQLCommitment[];
  isSimple?: boolean;
}) => {
  // TODO: UAT didn't think this alert depended on whether the payment methods were associated with commitments or not
  // Open question with UX
  // return paymentMethodsWithIssues.length > 0;

  if (!paymentMethodsWithIssues.length) {
    return false;
  }
  if (isSimple) {
    return true;
  }
  return associatedCommitments.length > 0;
};
const VerbosePaymentMethodAlert = t0 => {
  const $ = _c(14);
  const {
    amountDue
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== t) {
    t2 = t("my:paymentMethodAlert.verbose.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== amountDue || $[4] !== t) {
    t3 = amountDue ? t("my:paymentMethodAlert.verbose.messageWithDue", {
      totalAmountDue: t("core:numbers.currencyCustom", {
        amount: amountDue
      })
    }) : t("my:paymentMethodAlert.verbose.message");
    $[3] = amountDue;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("my:paymentMethodAlert.verbose.linkText");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <Link href="/billing/settings/">{t4}</Link>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t5) {
    t6 = <Alert title={t2} message={t3} variant="negative" dismissible={false} action={t5} />;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
const SimplePaymentMethodAlert = () => {
  const $ = _c(4);
  const {
    t
  } = useTranslation("my");
  let t0;
  if ($[0] !== t) {
    t0 = t("paymentMethodAlert.normal.message");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <Alert message={t0} variant="negative" dismissible={false} />;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const PaymentMethodAlert: React.FC<PaymentMethodAlertProps> = t0 => {
  const $ = _c(5);
  const {
    isSimple: t1
  } = t0;
  const isSimple = t1 === undefined ? false : t1;
  const {
    paymentMethodsWithIssues,
    associatedCommitments
  } = usePaymentMethodsWithIssues();
  if (!shouldRenderPaymentMethodAlert({
    paymentMethodsWithIssues,
    associatedCommitments,
    isSimple
  })) {
    return null;
  }
  if (isSimple) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SimplePaymentMethodAlert />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] !== associatedCommitments) {
    t2 = getCommitmentsTotalDue(associatedCommitments.filter(_temp));
    $[1] = associatedCommitments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <VerbosePaymentMethodAlert amountDue={t2} />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
function _temp(commitment) {
  return commitment.hasMissedPayment;
}